<template>
  <div class="img-canvas-wrapper">
    <canvas
      class="image"
      ref="imageCanvas"
      @mousedown.left.prevent="canvasMouseDownHandler"
      @click.right="canvasRightClickHandler"
      @click.left="canvasLeftClickHandler"
      @touchstart="canvasLeftClickHandler"
      @mouseup.left.prevent="canvasMouseUpHandler"
      @mousemove.passive="canvasMouseMoveHandler"
      @touchmove="canvasMouseMoveHandler"
      @mousewheel.prevent="scrollHandler"
      @mouseover="moseoverHandler"
      @mouseleave="moseleaveHandler"
      @dblclick.prevent="dblclickHandler"
      @keyup.space.prevent="changeMode()"
      @keyup.ctrl.90="undoLine"
      tabindex="0"
      v-show="!isImgLoad"
    >
    </canvas>
    <loader v-if="isImgLoad" />
    <canvas
      class="fabric-canvas"
      :class="!isOutlineMode && 'hidden-canvas'"
      ref="fabricCanvas"
    ></canvas>
    <canvas class="line-canvas" ref="lineCanvas"></canvas>
  </div>
</template>

<script>
import * as fabric from "fabric";
import { defaultImageFilters } from "@/constants";
import Loader from "@/components/Loader";

export default {
  name: "ImageCanvas",
  components: {
    Loader,
  },
  props: {
    cars: {
      type: Array,
      default() {
        return [];
      },
    },
    overlay: {
      type: Object,
      default() {
        return {
          active: false,
          activeImgId: null,
          images: [],
          lines: [],
          opacity: 1,
          imageOpacity: 1,
          hasBackgroundImage: true,
        };
      },
    },
    selectedCar: {
      type: Object,
    },
    isDrawBoxes: {
      type: Boolean,
      default: false,
    },
    savedLines: {
      type: Array,
      default() {
        return [];
      },
    },
    images: {
      type: Array,
      default() {
        return [];
      },
    },
    isBaseLine: {
      type: Boolean,
      default: false,
    },
    selectedImageIndex: {
      type: Number,
      default: -1,
    },
    asideWidth: {
      type: Number,
      default: 0,
    },
    options: {
      type: Object,
      default() {
        return {
          mode: "moving",
          lineWidth: 5,
          lineColor: "rgb(255, 0, 0)",
          sketchType: "pencil",
          bBox: false,
        };
      },
    },
  },
  data() {
    return {
      imageCanvasCtx: null,
      fabricCanvasCtx: null,
      lineCanvasCtx: null,
      imageObjects: [],
      compareImage: null,
      isImagesLoading: false,
      isDrag: false,
      isDragStarted: false,
      imgX: 0,
      imgY: 0,
      x: 0,
      y: 0,
      size: 0,
      isImgLoad: true,
      isOverlayImageLoad: false,
      selected: null,
      isDrawing: false,
      lines: [],
      isRedrawEnd: true,
      imagesList: [],
      overlayCache: {},
      overlayImages: [],
      overlayLines: [],
      staticWidth: 0,
      staticHeight: 0,
      currentImage: {},
      currentZoom: 1,
      zoomSpeed: 1.05,
      fabricPosition: {
        top: 0,
        left: 0,
        size: 1,
        angle: 0,
        hasChanges: false,
      },
    };
  },
  computed: {
    currentImages() {
      return this.isOverlayMode ? this.overlayImages : this.imagesList;
    },
    currentLines() {
      return this.isOverlayMode ? this.overlayLines : this.lines;
    },
    initialOverlayPosition() {
      const initialOverlayImg = this.overlay.images.find(
        (el) => el?.overlay_info?.position?.x || el?.overlay_info?.position?.y
      );
      return initialOverlayImg?.overlay_info ?? {};
    },
    isDrawMode() {
      return this.mode === "drawing";
    },
    isOverlayMode() {
      return this.overlay?.active;
    },
    isOutlineMode() {
      return this.isOverlayMode && this.overlay?.activeImgId;
    },
    showCanvas() {
      return this.isOutlineMode ? 2 : -1;
    },
    moveCanvas() {
      return this.isMovingMode || this.isDrawMode ? "none" : "auto";
    },
    blockFabricCanvas() {
      return !this.overlay.loading ? "none" : "auto";
    },
    isZoomMode() {
      return this.mode === "zoom";
    },
    isSingleImageMode() {
      return this.mode === "single";
    },
    isShowSaveBtn() {
      if (this.isImgLoad) {
        return false;
      }
      if (this.imagesList.length === 1) {
        return this.lines.length !== this.savedLines.length;
      }
      if (this.isOverlayMode) {
        return (
          this.overlayLines.length !== this.overlay.lines.length ||
          this.fabricPosition.hasChanges
        );
      }
      const image = this.imagesList.find((img, index) => {
        const { size, position } = this.images[index].compare_info;
        return (
          size !== img.size ||
          position?.x !== img.innerCordX ||
          position?.y !== img.innerCordY
        );
      });
      return this.lines.length !== this.savedLines.length || !!image;
    },
    mode() {
      return this.options.mode;
    },
    isMovingMode() {
      return this.mode === "moving";
    },
    lineColor() {
      return this.options.lineColor;
    },
    lineWidth() {
      return this.options.lineWidth;
    },
    selectedOverlayImgId() {
      return this.overlay?.activeImgId ?? false;
    },
  },
  methods: {
    init() {
      this.$emit("setCanvasImageLoading", this.isImgLoad);
      this.imageCanvasCtx = this.$refs.imageCanvas.getContext("2d");
      this.lineCanvasCtx = this.$refs.lineCanvas.getContext("2d");

      this.initFabricCanvas();

      this.$refs.imageCanvas.width =
        this.$refs.imageCanvas.parentNode.offsetWidth;
      this.$refs.imageCanvas.height =
        this.$refs.imageCanvas.parentNode.offsetHeight;

      this.$refs.lineCanvas.width = this.$refs.imageCanvas.width;
      this.$refs.lineCanvas.height = this.$refs.imageCanvas.height;

      this.lines = [...this.savedLines];
      this.staticWidth = this.images[0].image_width;
      this.staticHeight = this.images[0].image_height;
      const imagePromise = this.images.map((image, index) =>
        this.createImage(image, index)
      );
      Promise.all(imagePromise).then((imageList) => {
        this.imagesList.push(...imageList);
        this.isImgLoad = false;
        this.$emit("setCanvasImageLoading", this.isImgLoad);
        this.setDefaultPosition();
        this.updateFabricViewportTransform();
        // add width and height here to avoid jumping toolBar
        this.fabricCanvasCtx.setWidth(this.$refs.imageCanvas?.width);
        this.fabricCanvasCtx.setHeight(this.$refs.imageCanvas?.height);
        this.reDrawImg();
      });
    },
    initFabricCanvas() {
      this.fabricCanvasCtx = new fabric.Canvas(this.$refs.fabricCanvas, {
        selection: false,
        backgroundColor: null,
        preserveObjectStacking: true,
      });

      fabric.FabricObject.prototype.setControlsVisibility({
        tl: true, //top-left
        mt: false, // middle-top
        tr: true, //top-right
        ml: false, //middle-left
        mr: false, //middle-right
        bl: true, // bottom-left
        mb: false, //middle-bottom
        br: true, //bottom-right
      });

      this.fabricCanvasCtx.on("object:moving", (event) => {
        this.handleObjectMoving(event);
        this.$set(this.fabricPosition, "hasChanges", true);
      });

      this.fabricCanvasCtx.on("object:scaling", (event) => {
        this.handleObjectScaling(event);
        this.$set(this.fabricPosition, "hasChanges", true);
      });

      this.fabricCanvasCtx.on("object:rotating", (event) => {
        this.handleObjectRotating(event);
        this.$set(this.fabricPosition, "hasChanges", true);
      });

      this.fabricCanvasCtx.on("mouse:wheel", (event) => {
        this.handleMouseWheel(event);
      });
    },

    handleMouseWheel(e) {
      const event = e.e;
      event.preventDefault();

      const delta = event.deltaY > 0 ? -1 : 1;
      const zoomFactor = delta > 0 ? this.zoomSpeed : 1 / this.zoomSpeed;

      const pointer = this.fabricCanvasCtx.getPointer(event);
      const zoomX = pointer.x;
      const zoomY = pointer.y;

      this.zoomFabricCanvases(zoomFactor, zoomX, zoomY);

      this.zoom(delta, event.offsetX, event.offsetY);
    },

    zoomFabricCanvases(zoomFactor) {
      const canvasCenterX = this.fabricCanvasCtx.width / 2;
      const canvasCenterY = this.fabricCanvasCtx.height / 2;

      const currentZoom = this.fabricCanvasCtx.getZoom();
      const newZoom = currentZoom * zoomFactor;

      this.fabricCanvasCtx.zoomToPoint(
        new fabric.Point(canvasCenterX, canvasCenterY),
        newZoom
      );
      this.fabricCanvasCtx.renderAll();
    },
    handleObjectMoving(e) {
      const target = e.target;
      this.fabricPosition.top = target.top;
      this.fabricPosition.left = target.left;
    },
    handleObjectScaling(e) {
      const target = e.target;
      this.fabricPosition.size = target.scaleX;
      this.fabricPosition.top = target.top;
      this.fabricPosition.left = target.left;
    },
    handleObjectRotating(e) {
      const target = e.target;
      this.fabricPosition.angle = target.angle;
    },
    setDefaultPosition() {
      this.size =
        (this.staticHeight * Math.ceil(this.imagesList.length / 2)) /
        this.$refs.imageCanvas?.height;
      this.imgY = 0;
      this.setDefaultImgX();
      this.updateFabricViewportTransform();
      this.reDrawImg();
    },
    setDefaultImgX() {
      if (this.$refs.imageCanvas) {
        this.imgX =
          this.isOverlayMode || this.imagesList.length === 1
            ? Math.round(
                (this.$refs.imageCanvas.width - this.staticWidth / this.size) /
                  2
              )
            : Math.round(
                this.$refs.imageCanvas.width / 2 - this.staticWidth / this.size
              );
        this.updateFabricViewportTransform();
      }
    },
    canvasMouseDownHandler(e) {
      this.x = e.offsetX - this.imgX;
      this.y = e.offsetY - this.imgY;
      if (this.isSingleImageMode) {
        this.currentImage = this.findImageByCord(e.offsetX, e.offsetY);
      }
      this.isDragStarted = this.isDrawMode ? false : true;
      if (this.isDrawMode) {
        this.isDrawing = true;
        this.imageCanvasCtx.moveTo(e.offsetX, e.offsetY);
        this.currentLines.push(
          this.createLine(e.offsetX, e.offsetY, this.options.sketchType)
        );
      }
    },
    canvasMouseUpHandler(e) {
      if (!this.isDrag && this.isZoomMode) {
        this.zoom(1, e.offsetX, e.offsetY);
        window.test = e;
      }
      this.isDrag = false;
      this.isDragStarted = false;
      this.isDrawing = false;
      this.clearEmptyLine();
      this.$set(this.fabricPosition, "hasChanges", true);
    },
    clearEmptyLine() {
      const lastLineIndex = this.currentLines.length - 1;
      if (
        this.currentLines.length &&
        this.currentLines[lastLineIndex].way.length <= 1
      ) {
        this.currentLines.pop();
      }
    },
    canvasMouseMoveHandler(e) {
      if (this.isDragStarted) {
        this.isDrag = true;
        if (this.isSingleImageMode) {
          if (this.currentImage) {
            this.currentImage.innerCordX -=
              (this.x - (e.offsetX - this.imgX)) * this.size;
            this.currentImage.innerCordY -=
              (this.y - (e.offsetY - this.imgY)) * this.size;
          }
          this.x = e.offsetX - this.imgX;
          this.y = e.offsetY - this.imgY;
        } else {
          this.imgX = e.offsetX - this.x;
          this.imgY = e.offsetY - this.y;
          this.updateFabricViewportTransform();
        }
        this.reDrawImg();
      }
      if (this.isDrawing) {
        const lastLineIndex = this.currentLines.length - 1;
        const x = (e.offsetX - this.imgX) * this.size;
        const y = (e.offsetY - this.imgY) * this.size;
        const { x: x1, y: y1 } = this.currentLines[lastLineIndex].way[0];

        switch (this.options.sketchType) {
          case "arrow":
            this.currentLines[lastLineIndex].way = this.drawArrow(
              { x: x1, y: y1 },
              { x, y },
              this.lineWidth * 10
            );
            break;
          case "line":
            this.currentLines[lastLineIndex].way[1] = { x, y };
            break;
          case "circle":
            this.currentLines[lastLineIndex].way[1] = { x, y };
            break;
          default:
            this.currentLines[lastLineIndex].way.push({ x, y });
        }
        this.reDrawImg();
      }
    },
    scrollHandler(e) {
      if (!this.isSingleImageMode) {
        this.zoom(e.wheelDelta, e.offsetX, e.offsetY);
      }
      if (this.isSingleImageMode) {
        this.zoomSingleImage(e.wheelDelta, e.offsetX, e.offsetY);
      }
      this.reDrawImg();
    },
    canvasRightClickHandler(e) {
      if (!this.isDrag && this.isZoomMode) {
        e.preventDefault();
        e.stopPropagation();
        this.zoom(-1, e.offsetX, e.offsetY);
        this.reDrawImg();
      }
    },
    canvasLeftClickHandler(e) {
      this.reDrawImg();
      if (
        !this.isDrag &&
        !this.isDrawMode &&
        !this.isSingleImageMode &&
        this.cars.length &&
        this.cars[0].car_detections
      ) {
        let x = e.offsetX - this.imgX;
        let y = e.offsetY - this.imgY;
        this.selected = this.cars.find((el) => {
          return (
            x > el.car_detections.bbox[0] / this.size &&
            y > el.car_detections.bbox[1] / this.size &&
            x <
              (el.car_detections.bbox[0] + el.car_detections.bbox[2]) /
                this.size &&
            y <
              (el.car_detections.bbox[1] + el.car_detections.bbox[3]) /
                this.size
          );
        });
        this.$emit("selectCar", this.selected || {});
        this.reDrawImg();
      }
    },
    drawImages() {
      this.clearFabricCanvas();
      this.clear2DCanvas();
      this.currentImages.forEach((image, index) => {
        if (
          this.isOverlayMode &&
          image?.image?._id === this.selectedOverlayImgId
        ) {
          this.addFabricImageToCanvas(image);
        } else {
          this.imageCanvasCtx.save();

          const x = this.imgX + image.tableCordX / this.size;
          const y = this.imgY + image.tableCordY / this.size;

          if (image.image?.filters) {
            const filters =
              this.images.find((img) => img._id === image.image._id)?.filters ||
              {};
            const contrast = filters?.contrast || defaultImageFilters.contrast;
            const brightness =
              filters?.brightness || defaultImageFilters.brightness;
            this.imageCanvasCtx.filter = `brightness(${brightness}) contrast(${contrast})`;
          }

          this.imageCanvasCtx.drawImage(
            image.imageEl,
            -image.innerCordX / image.size,
            -image.innerCordY / image.size,
            image.image.image_width / image.size,
            image.image.image_height / image.size,
            x,
            y,
            this.staticWidth / this.size,
            this.staticHeight / this.size
          );
          this.drawCarInfo(image.image, x, y, index);
          this.imageCanvasCtx.restore();
        }
      });
    },
    clearFabricCanvas() {
      if (this.fabricCanvasCtx) {
        this.fabricCanvasCtx.clear();
        this.fabricCanvasCtx.renderAll();
      }
    },
    clear2DCanvas() {
      if (this.imageCanvasCtx) {
        this.imageCanvasCtx.save();
        this.imageCanvasCtx.setTransform(1, 0, 0, 1, 0, 0);
        this.imageCanvasCtx.clearRect(
          0,
          0,
          this.$refs.imageCanvas?.width,
          this.$refs.imageCanvas?.height
        );
        this.imageCanvasCtx.restore();
      }
    },

    updateFabricCanvasInitPosition() {
      const viewportTransform = this.fabricCanvasCtx.viewportTransform;
      const zoom = viewportTransform[0];
      const translateX = viewportTransform[4];
      const translateY = viewportTransform[5];

      const canvasCenterX =
        (this.fabricCanvasCtx.getWidth() / 2 - translateX) / zoom;
      const canvasCenterY =
        (this.fabricCanvasCtx.getHeight() / 2 - translateY) / zoom;

      return { canvasCenterX, canvasCenterY };
    },

    addFabricImageToCanvas(image) {
      if (!this.fabricCanvasCtx || !image) return;

      const imageType = image.image.isOutline ? "outline" : "normal";
      const uniqueId = `${image.image._id}_${imageType}`;
      const parentId = image.image._id;

      let existingGroup = this.fabricCanvasCtx
        .getObjects()
        .find((obj) => obj.id === parentId && obj.type === "group");

      if (existingGroup) {
        existingGroup.forEachObject((obj) => {
          if (obj.id === uniqueId) {
            existingGroup.remove(obj);
          }
        });
        if (existingGroup.getObjects().length === 0) {
          this.fabricCanvasCtx.remove(existingGroup);
          existingGroup = null;
        }
      }

      const { canvasCenterX, canvasCenterY } =
        this.updateFabricCanvasInitPosition();

      if (!this.fabricPosition.left) {
        this.fabricPosition.left = canvasCenterX;
      }
      if (!this.fabricPosition.top) {
        this.fabricPosition.top = canvasCenterY;
      }
      const imgScale = this.fabricPosition.size || 1 / this.size;
      const imgScaleSec = image.image.isOutline ? imgScale : 1;

      const angle = image.image.isOutline ? this.fabricPosition.angle || 0 : 0;

      const fabricImg = new fabric.FabricImage(image.imageEl, {
        left: this.fabricPosition.left,
        top: this.fabricPosition.top,
        originX: "center",
        originY: "center",
        angle: angle,
        scaleX: imgScaleSec,
        scaleY: imgScaleSec,
        selectable: true,
        hasControls: true,
        id: uniqueId,

        stroke: "#336dd1",
        strokeWidth: 5,

        borderColor: "#336dd1",
        cornerColor: "white",
        cornerStrokeColor: "#336dd1",
        borderScaleFactor: 1,
        cornerSize: 10,
        transparentCorners: false,
      });

      if (existingGroup) {
        existingGroup.add(fabricImg);
      } else {
        const newGroup = new fabric.Group([fabricImg], {
          left: fabricImg.left,
          top: fabricImg.top,
          angle: this.fabricPosition.angle || 0,
          originX: "center",
          originY: "center",
          selectable: true,
          hasControls: true,
          id: parentId,

          borderColor: "#336dd1",
          cornerColor: "white",
          cornerStrokeColor: "#336dd1",
          borderScaleFactor: 1,
          cornerSize: 10,
          transparentCorners: false,
          scaleX: imgScale,
          scaleY: imgScale,
        });

        this.fabricCanvasCtx.add(newGroup);
      }

      const imageOpacity = image.image.isOutline
        ? this.overlay.opacity
        : this.overlay.imageOpacity;
      fabricImg.set("opacity", imageOpacity);

      this.fabricCanvasCtx.renderAll();
    },
    drawGrid() {
      if (this.imagesList.length > 1) {
        this.imageCanvasCtx.save();
        this.imageCanvasCtx.lineWidth = 15 / this.size;
        this.imageCanvasCtx.strokeStyle = "#fffff";
        this.imageCanvasCtx.moveTo(
          this.staticWidth / this.size + this.imgX,
          this.imgY
        );
        this.imageCanvasCtx.lineTo(
          this.staticWidth / this.size + this.imgX,
          (this.staticHeight * Math.ceil(this.imagesList.length / 2)) /
            this.size +
            this.imgY
        );
        for (
          let index = 0;
          index < Math.ceil(this.imagesList.length / 2) + 1;
          index++
        ) {
          this.imageCanvasCtx.moveTo(
            this.imgX,
            (this.staticHeight * index) / this.size + this.imgY
          );
          this.imageCanvasCtx.lineTo(
            (this.staticWidth / this.size) * 2 + this.imgX,
            (this.staticHeight * index) / this.size + this.imgY
          );
        }
        this.imageCanvasCtx.stroke();
        this.imageCanvasCtx.beginPath();
        this.imageCanvasCtx.restore();
      }
    },
    applyTransformations(ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      const centerX = ctx.canvas.width / 2;
      const centerY = ctx.canvas.height / 2;

      ctx.translate(centerX, centerY);
      ctx.scale(this.currentZoom, this.currentZoom);
      ctx.translate(-centerX, -centerY);
    },

    reDrawImg() {
      if (this.isImagesLoading) {
        return;
      }
      if (this.isRedrawEnd) {
        this.isRedrawEnd = false;
        this.imageCanvasCtx.clearRect(
          0,
          0,
          this.$refs.imageCanvas?.width,
          this.$refs.imageCanvas?.height
        );
        this.lineCanvasCtx.clearRect(
          0,
          0,
          this.$refs.lineCanvas?.width,
          this.$refs.lineCanvas?.height
        );

        this.applyTransformations(this.imageCanvasCtx);
        this.applyTransformations(this.lineCanvasCtx);

        if (this.isOverlayMode) {
          const allObjects = this.fabricCanvasCtx.getObjects();
          allObjects.forEach((obj) => {
            this.fabricCanvasCtx.remove(obj);
          });
          this.drawImages();
          this.drawSketches();
        } else {
          this.drawImages();
          this.drawBoxes();
          this.drawGrid();
          this.drawBorderSelectedImage();
          this.drawSketches();
        }
        this.imageCanvasCtx.restore();
        this.lineCanvasCtx.restore();
        this.isRedrawEnd = true;
      }
    },
    zoom(direction, x, y) {
      this.x = x - this.imgX;
      this.y = y - this.imgY;
      if (direction < 0) {
        this.size *= this.zoomSpeed;
        this.imgX = this.imgX + this.x * (1 - 1 / this.zoomSpeed);
        this.imgY = this.imgY + this.y * (1 - 1 / this.zoomSpeed);
      } else if (direction > 0) {
        this.size *= 1 / this.zoomSpeed;
        this.imgX = this.imgX - this.x * Math.abs(1 - this.zoomSpeed);
        this.imgY = this.imgY - this.y * Math.abs(1 - this.zoomSpeed);
      }

      this.updateFabricViewportTransform();
      this.reDrawImg();
    },
    updateFabricViewportTransform() {
      if (this.fabricCanvasCtx) {
        const zoomFactor = 1 / this.size; // Since the main canvas uses this.size for scaling

        // Set the viewport transform
        this.fabricCanvasCtx.setViewportTransform([
          zoomFactor, // scaleX
          0, // skewX
          0, // skewY
          zoomFactor, // scaleY
          this.imgX, // translateX
          this.imgY, // translateY
        ]);

        // Request a render of the fabric.js canvas
        this.fabricCanvasCtx.requestRenderAll();
      }
    },

    zoomSingleImage(direction, offsetX, offsetY) {
      this.currentImage = this.findImageByCord(offsetX, offsetY);
      if (this.currentImage) {
        let x =
          (offsetX - this.imgX) * this.size -
          this.currentImage.tableCordX -
          this.currentImage.innerCordX;
        let y =
          (offsetY - this.imgY) * this.size -
          this.currentImage.tableCordY -
          this.currentImage.innerCordY;
        if (direction > 0) {
          this.currentImage.size *= this.zoomSpeed;
          this.currentImage.innerCordX -= x * Math.abs(1 - this.zoomSpeed);
          this.currentImage.innerCordY -= y * Math.abs(1 - this.zoomSpeed);
        } else if (direction < 0) {
          this.currentImage.size *= 1 / this.zoomSpeed;
          this.currentImage.innerCordX += x * (1 - 1 / this.zoomSpeed);
          this.currentImage.innerCordY += y * (1 - 1 / this.zoomSpeed);
        }
      }
    },
    moseleaveHandler() {
      this.isDrag = false;
      this.isDragStarted = false;
      this.isDrawing = false;
      this.$refs.imageCanvas.blur();
    },
    moseoverHandler() {
      this.$refs.imageCanvas.focus();
    },
    dblclickHandler(e) {
      if (!this.isZoomMode && !this.isSingleImageMode) {
        this.setDefaultPosition();
      }
      if (this.isSingleImageMode) {
        this.setDefaultSingleImagePosition(e.offsetX, e.offsetY);
      }
    },
    setDefaultSingleImagePosition(offsetX, offsetY) {
      const targetImg = this.findImageByCord(offsetX, offsetY);
      if (!targetImg) {
        return;
      }
      this.currentImage = targetImg;
      this.currentImage.size = 1;
      if (this.isOverlayMode) {
        const { x, y } = this.getDefaultOverlayInnerCoord(targetImg.image);
        this.currentImage.innerCordX = x;
        this.currentImage.innerCordY = y;
      } else {
        this.currentImage.innerCordX = 0;
        this.currentImage.innerCordY = 0;
      }
      this.reDrawImg();
    },
    drawCarInfo(image, x, y, index) {
      if (!this.imageCanvasCtx || this.isOverlayMode || !this.options.bBox) {
        return;
      }
      this.imageCanvasCtx.save();

      this.imageCanvasCtx.font = "20px Open Sans";
      this.imageCanvasCtx.fillStyle = "rgba(0, 255, 0, 1)";
      this.imageCanvasCtx.textAlign = index ? "left" : "right";
      this.imageCanvasCtx.textBaseline = "top";

      const label = this.label(image.tags?.[0]);

      const posX = index === 0 ? x + this.staticWidth / this.size - 20 : x + 20;
      const posY = y + 10;

      this.imageCanvasCtx.fillText(label, posX, posY);
      this.imageCanvasCtx.restore();
    },
    drawBoxes() {
      if (
        this.cars.length &&
        this.isDrawBoxes &&
        this.cars[0]?.car_detections
      ) {
        this.imageCanvasCtx.save();
        for (let i = 0; i < this.cars.length; i++) {
          let [x, y, w, h] = this.cars[i].car_detections.bbox;
          // let [numberX, numberY, numberW, numberH] = this.cars[i].number_detections[0].bbox
          this.imageCanvasCtx.strokeStyle = "rgb(0, 255, 0)";
          this.imageCanvasCtx.fillStyle = "rgba(100, 255, 100, 0.15)";
          if (this.selected && this.selected._id === this.cars[i]._id) {
            this.imageCanvasCtx.fillRect(
              this.imgX + x / this.size,
              this.imgY + y / this.size,
              w / this.size,
              h / this.size
            );
          }
          this.imageCanvasCtx.strokeRect(
            this.imgX + x / this.size,
            this.imgY + y / this.size,
            w / this.size,
            h / this.size
          );
          // this.imageCanvasCtx.strokeRect(this.imgX + ((x + numberX) / this.size), this.imgY + ((y + numberY) / this.size), numberW / this.size, numberH / this.size)
          this.imageCanvasCtx.font =
            Math.round(40 / this.size) + "px Open Sans";
          this.imageCanvasCtx.fillStyle = "rgba(0, 255, 0, 1)";
          let label = this.label(this.cars[i]);
          this.imageCanvasCtx.fillText(
            label,
            this.imgX + x / this.size,
            this.imgY + (y - 15) / this.size
          );
        }
        this.imageCanvasCtx.restore();
      }
    },
    label(tag) {
      let car_number = tag.car_number !== "n/a" ? `${tag.car_number}` : "";
      let car_model = tag.car_model !== "n/a" ? ` ${tag.car_model} ` : "";
      let car_orientation =
        tag.car_orientation !== "n/a" ? ` ${tag.car_orientation}` : "";
      return car_number + car_orientation + car_model;
    },
    createLine(x, y, sketchType) {
      return {
        typeFigure: sketchType,
        color: this.lineColor,
        width: this.lineWidth,
        way: [
          { x: (x - this.imgX) * this.size, y: (y - this.imgY) * this.size },
        ],
      };
    },
    drawSketches() {
      this.lineCanvasCtx.save();
      this.lineCanvasCtx.lineJoin = "round";
      this.lineCanvasCtx.lineCap = "round";

      this.lineCanvasCtx.beginPath();
      this.lineCanvasCtx.stroke();
      this.lineCanvasCtx.beginPath();
      for (let i = 0; i < this.currentLines.length; i++) {
        let line = this.currentLines[i];
        this.lineCanvasCtx.lineWidth = line.width / this.size;
        this.lineCanvasCtx.strokeStyle = line.color;
        switch (line.typeFigure) {
          case "circle":
            this.drawCircle(line.way[0], line.way[1]);
            break;
          default:
            this.drawLine(line);
        }
        this.lineCanvasCtx.stroke();
        this.lineCanvasCtx.beginPath();
      }
      this.lineCanvasCtx.restore();
    },
    drawCircle(start, end) {
      const convertStart = {
        x: start.x / this.size + this.imgX,
        y: start.y / this.size + this.imgY,
      };
      const convertEnd = {
        x: end.x / this.size + this.imgX,
        y: end.y / this.size + this.imgY,
      };
      const x = (convertStart.x + convertEnd.x) / 2;
      const y = (convertStart.y + convertEnd.y) / 2;
      const verticalRadius = Math.abs(y - convertStart.y);
      const horisantalRadius = Math.abs(x - convertStart.x);
      this.lineCanvasCtx.ellipse(
        x,
        y,
        horisantalRadius,
        verticalRadius,
        0,
        0,
        2 * Math.PI
      );
    },
    drawLine(line) {
      this.lineCanvasCtx.moveTo(
        line.way[0].x / this.size + this.imgX,
        line.way[0].y / this.size + this.imgY
      );
      let step = 1;
      for (let j = 1; j < line.way.length; j += step) {
        this.lineCanvasCtx.lineTo(
          line.way[j].x / this.size + this.imgX,
          line.way[j].y / this.size + this.imgY
        );
      }
    },
    drawBorderSelectedImage() {
      if (this.selectedImageIndex === -1) return;
      this.imageCanvasCtx.save();
      const image = this.imagesList[this.selectedImageIndex];
      this.imageCanvasCtx.lineWidth = 15 / this.size;
      this.imageCanvasCtx.strokeStyle = "#00ff00";
      const x = this.imgX + image.tableCordX / this.size;
      const y = this.imgY + image.tableCordY / this.size;
      this.imageCanvasCtx.strokeRect(
        x,
        y,
        image.image.image_width / this.size,
        image.image.image_height / this.size
      );
      this.imageCanvasCtx.restore();
    },
    changeMode(mode) {
      this.$emit(
        "changeMode",
        "mode",
        mode || this.mode === "drawing" ? "moving" : "drawing"
      );
    },
    undoLine() {
      if (this.currentLines?.length) {
        this.currentLines.pop();
        this.reDrawImg();
      }
    },
    getDefaultOverlayInnerCoord(img) {
      if (!img) {
        return { x: 0, y: 0 };
      }
      return {
        x:
          img?.overlay_info?.position?.x ??
          (img.image_width - img.overlay_width) / 2,
        y:
          img?.overlay_info?.position?.y ??
          (img.image_height - img.overlay_height) / 2,
      };
    },
    getFormattedOverlayImage(image) {
      const { x, y } = this.getDefaultOverlayInnerCoord(image);
      return {
        image,
        imageEl: image.imageEl,
        innerCordX: x,
        innerCordY: y,
        tableCordX: 0,
        tableCordY: 0,
        size: image?.overlay_info?.size ?? 1,
      };
    },
    getOverlayCache(id) {
      return this.overlayCache[id];
    },
    resetOverlayCache() {
      this.overlayCache = {};
    },
    setCurrentOverlayImg() {
      this.isImagesLoading = true;
      const base = this.imagesList.find(
        (el) => el?.image?._id !== this.selectedOverlayImgId
      );
      const basePicture = {
        ...base,
        innerCordX: 0,
        innerCordY: 0,
        tableCordX: 0,
        tableCordY: 0,
        size: 1,
      };
      const overlayBase = this.overlay.images.find(
        (el) => el._id === this.selectedOverlayImgId
      );
      const outlineOverlayImg = {
        ...overlayBase,
        isOutline: true,
      };
      const formattedOutlineImage = this.createImage(
        outlineOverlayImg,
        0,
        true
      );
      const formattedImage = this.getFormattedOverlayImage(overlayBase);
      const { image, outlineImage, fabricPosition } =
        this.getOverlayCache(this.selectedOverlayImgId) || {};
      if (image && outlineImage) {
        this.overlayImages = [basePicture, image, outlineImage];
        if (fabricPosition) {
          this.fabricPosition = fabricPosition;
        }
        this.isImagesLoading = false;
        return;
      } else {
        if (overlayBase?.overlay_info?.position?.fabricPosition) {
          const { angle, left, size, top } =
            overlayBase.overlay_info.position.fabricPosition;
          this.fabricPosition = { angle, left, size, top };
        } else {
          const { canvasCenterX, canvasCenterY } =
            this.updateFabricCanvasInitPosition();
          this.fabricPosition.left = canvasCenterX;
          this.fabricPosition.top = canvasCenterY;
          this.fabricPosition.angle = 0;
        }
      }

      Promise.all([formattedOutlineImage]).then(([outlineImg]) => {
        this.overlayImages = [basePicture, formattedImage, outlineImg];
        this.isImagesLoading = false;
        this.reDrawImg();
      });
    },
    setCurrentOverlayLines() {
      const { lines } = this.getOverlayCache(this.selectedOverlayImgId) || {};
      this.overlayLines = lines ? [...lines] : [];
    },
    setOverlayCache(id) {
      this.overlayCache[id] = {
        image: this.overlayImages.find(
          (el) => el?.image?._id === id && !el?.image?.isOutline
        ),
        outlineImage: this.overlayImages.find(
          (el) => el?.image?._id === id && el?.image?.isOutline
        ),
        lines: this.overlayLines,
        fabricPosition: JSON.parse(JSON.stringify(this.fabricPosition)),
      };
    },
    saveOverlay() {
      const payload = {
        lines: this.overlayLines,
        overlay: [],
      };
      const overlayImagesModified = this.overlayImages.filter(
        (img) => !img.image?.isOutline
      );
      overlayImagesModified.forEach((img, index) => {
        const halfIndex = Math.floor(index / 2);
        const imageId = img.image._id;

        if (!payload.overlay[halfIndex]) {
          payload.overlay[halfIndex] = {};
        }

        const targetImg = imageId !== this.selectedOverlayImgId ? null : img;
        payload.overlay[halfIndex][(index % 2) + ""] = {
          image_id: imageId,
          position: {
            x: targetImg?.innerCordX ?? null,
            y: targetImg?.innerCordY ?? null,
            fabricPosition:
              this.getOverlayCache(imageId)?.fabricPosition ??
              this.fabricPosition,
          },
          size: targetImg?.size ?? null,
        };
      });
      this.$emit("saveImage", payload);
      this.clearSecondImageLinesCache();
      this.$set(this.fabricPosition, "hasChanges", false);
    },
    clearSecondImageLinesCache() {
      const secondImageId = this.overlayImages.find(
        (img) => img.image?._id !== this.selectedOverlayImgId
      )?.image._id;

      if (secondImageId && this.overlayCache[secondImageId]) {
        this.overlayCache[secondImageId].lines = [];
      }
    },
    updateImage() {
      if (this.isOverlayMode) {
        this.saveOverlay();
      } else {
        this.saveImage();
      }
    },
    saveImage() {
      let image = {};
      image.lines = this.lines;
      if (this.imagesList.length > 1) {
        if (!this.isBaseLine) {
          image.compare = [{}];
          this.imagesList.forEach((img, index) => {
            image.compare[0][index + ""] = {
              image_id: img.image._id,
              position: { x: img.innerCordX, y: img.innerCordY },
              size: img.size,
            };
          });
        } else {
          image.compare = [];
          this.imagesList.forEach((img, index) => {
            if (!image.compare[Math.floor(index / 2)]) {
              image.compare[Math.floor(index / 2)] = {};
            }
            image.compare[Math.floor(index / 2)][(index % 2) + ""] = {
              image_id: img.image._id,
              position: { x: img.innerCordX, y: img.innerCordY },
              size: img.size,
            };
          });
        }
      }
      this.$emit("saveImage", image);
    },
    createImage(image, index, useOutline = false) {
      const imageEl = new Image();
      imageEl.src =
        useOutline && image.overlay_outline_url
          ? image.overlay_outline_url
          : image.url;

      const innerCordX = image?.compare_info?.position?.x ?? 0;
      const innerCordY = image?.compare_info?.position?.y ?? 0;
      const column = index % 2 === 0 ? 0 : 1;
      const row = Math.floor(index / 2);
      const tableCordX = column * this.staticWidth;
      const tableCordY = row * this.staticHeight;
      const formatedImg = {
        image,
        imageEl,
        innerCordX,
        innerCordY,
        tableCordX,
        tableCordY,
        size: image?.compare_info?.size ?? 1,
      };
      return new Promise((resolve) => {
        imageEl.onload = () => resolve(formatedImg);
      });
    },
    findImageByCord(x, y) {
      return this.currentImages.find((image) => {
        if (
          this.isOverlayMode &&
          image?.image?._id !== this.selectedOverlayImgId
        ) {
          return false;
        }
        const imageX = image.tableCordX / this.size + this.imgX;
        const imageY = image.tableCordY / this.size + this.imgY;
        const imageWidth = this.staticWidth / this.size;
        const imageHeight = this.staticHeight / this.size;
        return (
          x > imageX &&
          y > imageY &&
          x < imageX + imageWidth &&
          y < imageY + imageHeight
        );
      });
    },
    drawArrow(a, b, arrowLength) {
      const phy = Math.PI / 3.5;

      const shift =
        (a.x - b.x > 0 && a.y - b.y < 0) || (a.x - b.x > 0 && a.y - b.y > 0)
          ? Math.PI
          : 0;
      const tangAlf = (b.y - a.y) / (b.x - a.x);
      const alf = Math.atan(tangAlf) + shift;
      const x1 = b.x - arrowLength * Math.sin(phy - alf);
      const y1 = b.y - arrowLength * Math.cos(phy - alf);
      const x2 = b.x - arrowLength * Math.sin(phy + alf);
      const y2 = b.y + arrowLength * Math.cos(phy + alf);

      return [
        a,
        b,
        {
          x: x1,
          y: y1,
        },
        b,
        {
          x: x2,
          y: y2,
        },
      ];
    },
  },
  watch: {
    mode() {
      this.fabricCanvasCtx.discardActiveObject();
      this.fabricCanvasCtx.renderAll();
    },
    isShowSaveBtn(value) {
      this.$emit("updateSaveButton", value);
    },
    "options.bBox"() {
      this.reDrawImg();
    },
    "overlay.active"() {
      this.setDefaultPosition();
    },
    "overlay.activeImgId"(curId, prevId) {
      if (!curId) {
        return;
      }
      this.setOverlayCache(prevId);
      this.setCurrentOverlayImg();
      this.setCurrentOverlayLines();
      this.reDrawImg();
    },
    "overlay.images"(value, prevValue) {
      if (!value?.length || value === prevValue) {
        return;
      }
      this.resetOverlayCache();
      this.setCurrentOverlayImg();
      this.reDrawImg();
    },
    "overlay.lines"(value, prevValue) {
      if (!value?.length || value === prevValue) {
        return;
      }
      this.overlayLines = value?.length ? [...value] : [];
      // this.reDrawImg() // TODO remove to avoid clear active fabric
    },
    "overlay.opacity"() {
      this.reDrawImg();
    },
    "overlay.imageOpacity"() {
      this.reDrawImg();
    },
    "overlay.hasBackgroundImage"() {
      this.reDrawImg();
    },
    selectedCar(value) {
      this.selected = value;
      this.reDrawImg();
    },
    savedLines: {
      deep: true,
      handler() {
        this.lines = [...this.savedLines];
        this.reDrawImg();
      },
    },
    isDrawBoxes() {
      this.reDrawImg();
    },
    selectedImageIndex() {
      this.reDrawImg();
    },
    asideWidth(val) {
      this.$refs.imageCanvas.width =
        this.$refs.imageCanvas.parentNode.offsetWidth - val;
      this.reDrawImg();
    },
  },
  mounted() {
    this.init();
    this.imageCanvasCtx.save();
  },
};
</script>

<style lang="scss" scoped>
.img-canvas-wrapper {
  width: 100%;
  height: 100%;
  position: relative;

  .fabric-canvas,
  .image,
  .canvas-container,
  .line-canvas {
    position: absolute;
    top: 0;
    left: 0;
  }
  .fabric-canvas {
    z-index: 2;
  }
  .line-canvas {
    z-index: 3;
    pointer-events: none;
  }
  :deep(.canvas-container) {
    z-index: v-bind(showCanvas);
    pointer-events: v-bind(moveCanvas);
    .upper-canvas {
      z-index: v-bind(showCanvas);
    }
  }

  .save-btn {
    position: absolute;
    top: 8px;
    left: 8px;
  }
}
</style>
